/**
 * Layer Type Schemas — Defines available settings for each layer type.
 * These schemas drive the builder UI automatically.
 */

import { LayerTypeSchema } from './types';

export const LAYER_SCHEMAS: LayerTypeSchema[] = [
  // ── Backgrounds ─────────────────────────────────────────────────────────────
  {
    type: 'solid-background',
    label: 'Solid Background',
    icon: 'Square',
    description: 'Solid color fill',
    settings: [
      { key: 'colorHue', label: 'Color', type: 'range', defaultValue: 0, min: 0, max: 360, step: 5, reactive: true },
      { key: 'saturation', label: 'Saturation', type: 'range', defaultValue: 0, min: 0, max: 100, step: 5, reactive: false },
      { key: 'lightness', label: 'Lightness', type: 'range', defaultValue: 5, min: 0, max: 50, step: 1, reactive: true },
    ],
  },
  {
    type: 'gradient-background',
    label: 'Gradient',
    icon: 'Palette',
    description: 'Audio-reactive color gradient that breathes with the beat',
    settings: [
      { key: 'baseHue', label: 'Base Color', type: 'range', defaultValue: 250, min: 0, max: 360, step: 10, reactive: true },
      { key: 'intensity', label: 'Intensity', type: 'range', defaultValue: 1.0, min: 0.3, max: 2, step: 0.1, reactive: true },
    ],
  },

  // ── Spectrum ────────────────────────────────────────────────────────────────
  {
    type: 'frequency-bars',
    label: 'Frequency Bars',
    icon: 'BarChart3',
    description: 'Vertical bars that respond to frequency data',
    settings: [
      { key: 'barCount', label: 'Bar Count', type: 'range', defaultValue: 64, min: 8, max: 128, step: 4, reactive: false },
      { key: 'gap', label: 'Gap', type: 'range', defaultValue: 2, min: 0, max: 8, step: 1, reactive: false },
      { key: 'colorHue', label: 'Color Shift', type: 'range', defaultValue: 0, min: 0, max: 360, step: 10, reactive: true },
      { key: 'sensitivity', label: 'Sensitivity', type: 'range', defaultValue: 1.2, min: 0.3, max: 3, step: 0.1, reactive: true },
    ],
  },
  {
    type: 'mirrored-bars',
    label: 'Mirrored Bars',
    icon: 'AlignVerticalSpaceAround',
    description: 'Symmetrical bars mirrored from center with glow',
    settings: [
      { key: 'barCount', label: 'Bar Count', type: 'range', defaultValue: 48, min: 16, max: 96, step: 8, reactive: false },
      { key: 'gap', label: 'Gap', type: 'range', defaultValue: 2, min: 0, max: 6, step: 1, reactive: false },
      { key: 'colorHue', label: 'Color Shift', type: 'range', defaultValue: 270, min: 0, max: 360, step: 10, reactive: true },
      { key: 'glowAmount', label: 'Glow', type: 'range', defaultValue: 12, min: 0, max: 30, step: 2, reactive: true },
    ],
  },
  {
    type: 'circular-bars',
    label: 'Circular Spectrum',
    icon: 'Circle',
    description: 'Radial frequency bars arranged in a spinning circle',
    settings: [
      { key: 'barCount', label: 'Bars', type: 'range', defaultValue: 90, min: 30, max: 180, step: 10, reactive: false },
      { key: 'baseRadius', label: 'Radius', type: 'range', defaultValue: 0.2, min: 0.1, max: 0.4, step: 0.02, reactive: true },
      { key: 'rotationSpeed', label: 'Spin Speed', type: 'range', defaultValue: 0.3, min: 0, max: 2, step: 0.1, reactive: true },
      { key: 'colorHue', label: 'Color Shift', type: 'range', defaultValue: 180, min: 0, max: 360, step: 10, reactive: true },
    ],
  },
  {
    type: 'waveform',
    label: 'Waveform Line',
    icon: 'Activity',
    description: 'Oscilloscope-style waveform with glow',
    settings: [
      { key: 'lineWidth', label: 'Line Width', type: 'range', defaultValue: 2.5, min: 1, max: 6, step: 0.5, reactive: true },
      { key: 'glowAmount', label: 'Glow', type: 'range', defaultValue: 15, min: 0, max: 40, step: 2, reactive: true },
      { key: 'colorHue', label: 'Color Shift', type: 'range', defaultValue: 160, min: 0, max: 360, step: 10, reactive: true },
    ],
  },

  // ── Particles ───────────────────────────────────────────────────────────────
  {
    type: 'particles',
    label: 'Particle Field',
    icon: 'Sparkles',
    description: 'Floating particles that react to bass and treble',
    settings: [
      { key: 'particleCount', label: 'Particles', type: 'range', defaultValue: 200, min: 50, max: 500, step: 25, reactive: false },
      { key: 'baseSpeed', label: 'Drift Speed', type: 'range', defaultValue: 0.3, min: 0.05, max: 1, step: 0.05, reactive: true },
      { key: 'colorHue', label: 'Color Shift', type: 'range', defaultValue: 260, min: 0, max: 360, step: 10, reactive: true },
    ],
  },
  {
    type: 'starfield',
    label: 'Star Field',
    icon: 'Star',
    description: 'Flying through stars — speed driven by audio energy',
    settings: [
      { key: 'starCount', label: 'Star Count', type: 'range', defaultValue: 400, min: 100, max: 800, step: 50, reactive: false },
      { key: 'baseSpeed', label: 'Base Speed', type: 'range', defaultValue: 0.3, min: 0.05, max: 1, step: 0.05, reactive: true },
    ],
  },

  // ── Minimal / Ambient ───────────────────────────────────────────────────────
  {
    type: 'glow-orb',
    label: 'Glow Orb',
    icon: 'Sun',
    description: 'Pulsing central orb with glow and orbiting dots',
    settings: [
      { key: 'baseSize', label: 'Orb Size', type: 'range', defaultValue: 0.15, min: 0.05, max: 0.3, step: 0.01, reactive: true },
      { key: 'colorHue', label: 'Color', type: 'range', defaultValue: 280, min: 0, max: 360, step: 10, reactive: true },
      { key: 'orbitSpeed', label: 'Orbit Speed', type: 'range', defaultValue: 0.5, min: 0, max: 2, step: 0.1, reactive: true },
    ],
  },
  {
    type: 'ring-pulse',
    label: 'Ring Pulse',
    icon: 'Target',
    description: 'Expanding ring shockwaves triggered by beats',
    settings: [
      { key: 'colorHue', label: 'Color', type: 'range', defaultValue: 200, min: 0, max: 360, step: 10, reactive: true },
      { key: 'ringWidth', label: 'Ring Width', type: 'range', defaultValue: 2, min: 1, max: 6, step: 0.5, reactive: false },
      { key: 'expandSpeed', label: 'Expand Speed', type: 'range', defaultValue: 0.15, min: 0.05, max: 0.4, step: 0.05, reactive: true },
      { key: 'threshold', label: 'Beat Threshold', type: 'range', defaultValue: 0.3, min: 0.1, max: 0.8, step: 0.1, reactive: false },
    ],
  },

  // ── Overlays ────────────────────────────────────────────────────────────────
  {
    type: 'vignette',
    label: 'Vignette',
    icon: 'Aperture',
    description: 'Dark edges overlay for depth and focus',
    settings: [
      { key: 'darkness', label: 'Darkness', type: 'range', defaultValue: 0.6, min: 0.1, max: 1, step: 0.05, reactive: true },
      { key: 'radius', label: 'Radius', type: 'range', defaultValue: 0.7, min: 0.3, max: 1, step: 0.05, reactive: false },
    ],
  },
];

/** Get the schema for a specific layer type */
export function getLayerSchema(type: string): LayerTypeSchema | undefined {
  return LAYER_SCHEMAS.find(s => s.type === type);
}

/** Get default settings for a layer type */
export function getDefaultSettings(type: string): Record<string, number | string | boolean> {
  const schema = getLayerSchema(type);
  if (!schema) return {};
  const defaults: Record<string, number | string | boolean> = {};
  for (const s of schema.settings) {
    defaults[s.key] = s.defaultValue;
  }
  return defaults;
}
